import { useCallback } from "react";
import { atom, PrimitiveAtom, useAtom } from "jotai";

import { Api } from "interfaces";
import { useGetUserDetails } from "auth/auth-state";

import {
  useGetTypesForFile,
  useGetDroppedColumns,
} from "modules/data-files/api";
import { useCreateProjectWithDataAndWorkspace } from "modules/projects/api";

import * as flowConfig from "flows/clustering/config";

import { useGetInputFile } from "./store";

const projectCreateStatus = atom<"not-started" | "in-progress" | "completed">(
  "not-started"
);

const projectCreateError = atom<string | Api.ApiError>(null) as PrimitiveAtom<
  string | Api.ApiError
>;

export const useGetCreate = () => {
  const createProject = useCreateProjectWithDataAndWorkspace();

  const inputFile = useGetInputFile();
  const colTypes = useGetTypesForFile(inputFile);
  const droppedColumns = useGetDroppedColumns(inputFile);

  const [status, setStatus] = useAtom(projectCreateStatus);
  const [error, setError] = useAtom(projectCreateError);

  const user = useGetUserDetails()?.email;

  const create = useCallback(async () => {
    if (!inputFile) {
      setError("Input Data is MANDATORY!!!");
      return;
    }
    setStatus("in-progress");
    setError(null);

    const projectSpec = {
      flow: flowConfig.FLOW_NAME,
      data_files: { [flowConfig.INPUT_DATA]: inputFile },
      meta_data: { key: "INIT", [flowConfig.ID_VARS_KEY]: [] },
    };

    const workspaceSpec = {
      key: "INIT",
      meta_data: {},
      annotations: {
        [flowConfig.RUN_ID]: "INIT",
        [flowConfig.CREATED_BY]: user,
        [flowConfig.TIME_STAMP]: new Date().valueOf(),
      },
      recipe: flowConfig.RECIPE,
      input_data: { [flowConfig.INPUT_DATA]: [inputFile] },
      cleanup_spec: {
        [inputFile]: {
          column_types: colTypes,
          columns_to_drop: droppedColumns,
        },
      },
      variable_mapping: {},
      model_params: {},
      hyper_params: {
        [flowConfig.MIN_NUM_TOPICS_KEY]: flowConfig.DEFAULT_MIN_TOPICS,
        [flowConfig.MAX_NUM_TOPICS_KEY]: flowConfig.DEFAULT_MAX_TOPICS,
        [flowConfig.BIN_COUNT_KEY]: flowConfig.DEFAULT_BIN_COUNT,
        [flowConfig.CUSTOM_BINS]: flowConfig.DEFAULT_CUSTOM_BINS,
      },
    };

    const resp = await createProject({
      "project-spec": projectSpec,
      "workspace-spec": workspaceSpec,
    });
    if (resp.success) {
    } else if (resp.success === false) {
      setError(resp.error);
    }
    setStatus("completed");
    return resp;
  }, [inputFile, user, colTypes, droppedColumns, createProject]);

  return [create, status, error] as const;
};
